import { useEffect, useState, useContext } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  NavDropdown,
  Table
} from "react-bootstrap";
import { getQualityCheckStatusKeyFromValue } from "../../helpers/stringHelper";
import {
  customFilters,
  customDateFilters
} from "../../helpers/tableViewHelper";
import {
  QualityCheckStatusUI,
  QualityCheckStatusType,
  CdlTableColumn,
  GetCdlTableOutput,
  ExtendedRepositoryObject
} from "../../backend/types";
import Error from "../abstractComponents/error";
import Success from "../abstractComponents/success";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import { useGetCdlCatalogList } from "../../backend/hooks/importExportMenu/cdl/queryGetCdlCatalogList";
import WaterIcon from "@mui/icons-material/Water";
import { DataGridPro, GridSelectionModel } from "@mui/x-data-grid-pro";
import { useGetCdlSchemaList } from "../../backend/hooks/importExportMenu/cdl/queryGetCdlSchemaList";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useImportCdl } from "../../backend/hooks/importExportMenu/cdl/mutationImportCdlData";
import { useGetCdlTableList } from "../../backend/hooks/importExportMenu/cdl/queryGetCdlTableList";

// Component that renders everything related to CDL Import
export default function ImportFromCdl(props: {
  currentFolder: string;
  disabled: boolean;
  currentSubArray: ExtendedRepositoryObject[];
}) {
  const { activityId, activityData } = useContext(PmxActivityDetailsContext);

  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState<String | null>(null);
  const [selectedSchema, setSelectedSchema] = useState<String | null>(null);
  const [selectedTable, setSelectedTable] = useState<String | null>(null);
  const [qualityCheckStatus, setQualityCheckStatus] =
    useState<QualityCheckStatusUI>(QualityCheckStatusUI.NOT_STARTED);
  const [description, setDescription] = useState("");
  const [pageSizeTable, setPageSizeTable] = useState(50);
  const [selectedSchemasForImport, setSelectedSchemasForImport] =
    useState<GridSelectionModel>([]);
  const [selectedTablesForImport, setSelectedTablesForImport] =
    useState<GridSelectionModel>([]);

  const { getCdlTableList, tableList, isLoadingTableList, errorTableList } =
    useGetCdlTableList();
  const {
    importCdlContentMutation,
    cdlImportMutationData,
    isLoadingCdlMutation,
    errorCdlMutation
  } = useImportCdl();
  const {
    getCdlCatalogList,
    catalogList,
    isLoadingCatalogList,
    errorCatalogList
  } = useGetCdlCatalogList();
  const { getCdlSchemaList, schemaList, isLoadingSchemaList, errorSchemaList } =
    useGetCdlSchemaList();

  const closeModal = () => {
    setIsImportModalVisible(false);
    setSelectedCatalog(null);
    setSelectedSchema(null);
    setSelectedTable(null);
    setSelectedSchemasForImport([]);
    setSelectedTablesForImport([]);
    setQualityCheckStatus(QualityCheckStatusUI.NOT_STARTED);
    setDescription("");
  };

  function changeQualityCheckedStatus(
    event: React.ChangeEvent<HTMLSelectElement>
  ) {
    event.preventDefault();
    setQualityCheckStatus(
      QualityCheckStatusUI[event.target.value as QualityCheckStatusType]
    );
  }
  const handleSelectMultipleSchemas = (e: GridSelectionModel) => {
    setSelectedSchemasForImport(e);
  };
  const handleSelectMultipleTables = (e: GridSelectionModel) => {
    setSelectedTablesForImport(e);
  };

  const openModal = () => {
    getCdlCatalogList();
    setIsImportModalVisible(true);
  };

  const createColumns = (setSelected: Function) => [
    {
      field: "name",
      headerName: "Name",
      flex: 3,
      filterOperators: customFilters(),
      renderCell: (params: any) => (
        <Button variant="link" onClick={() => setSelected(params.row.name)}>
          {params.row.name}
        </Button>
      ),
      valueGetter: (params: any) => params.row.name
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 2,
      type: "date",
      filterOperators: customDateFilters
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      flex: 2,
      filterOperators: customFilters()
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 2,
      hide: true,
      type: "date",
      filterOperators: customDateFilters
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 2,
      hide: true,
      filterOperators: customFilters()
    }
  ];

  const catalogColumns = [
    ...createColumns(setSelectedCatalog),
    {
      field: "catalogType",
      headerName: "Catalog Type",
      flex: 1,
      filterOperators: customFilters()
    }
  ];
  const schemaColumns = createColumns(setSelectedSchema);
  const tableColumns = createColumns(setSelectedTable);

  useEffect(() => {
    if (selectedCatalog) {
      getCdlSchemaList({
        variables: {
          catalogName: selectedCatalog
        }
      });
    }
  }, [selectedCatalog]);

  useEffect(() => {
    if (selectedSchema) {
      const schemaSplit = selectedSchema.split(".");
      getCdlTableList({
        variables: {
          catalogName: selectedCatalog,
          schemaName: schemaSplit[schemaSplit.length - 1]
        }
      });
    }
  }, [selectedSchema]);

  const importSchemaCallerSchema = () => {
    let weHaveSameNameSomewhere = false;
    props.currentSubArray.forEach((file: any) => {
      selectedSchemasForImport.forEach((schema: string) => {
        if (
          file &&
          file.name &&
          (file.name === schema || file.name === schema + "/")
        ) {
          weHaveSameNameSomewhere = true;
        }
      });
      selectedTablesForImport.forEach((table: string) => {
        const name = table.split(".").pop();
        if (
          file &&
          file.name &&
          (file.name === name || file.name === `${name}.csv`)
        ) {
          weHaveSameNameSomewhere = true;
        }
      });
    });

    if (
      weHaveSameNameSomewhere &&
      !window.confirm(
        "This import might overwrite files. Do you want to continue?"
      )
    ) {
      return;
    }

    importCdlContentMutation({
      variables: {
        activityId: activityId,
        targetFolder: props.currentFolder,
        cdlPaths:
          selectedTablesForImport.length > 0
            ? selectedTablesForImport
            : selectedSchemasForImport,
        description: description,
        qualityCheckStatus:
          getQualityCheckStatusKeyFromValue(qualityCheckStatus)
      }
    }).then(() => {
      closeModal();
    });
  };

  return (
    <>
      <NavDropdown.Item
        size="small"
        variant="outlined"
        onClick={openModal}
        disabled={props.disabled}
        id="importFromCdlItem"
      >
        <WaterIcon /> CDL Import
      </NavDropdown.Item>

      {cdlImportMutationData?.importCdlData && (
        <Success message="CDL import started. You will receive a notification in up to 15 Minutes" />
      )}
      {(errorCatalogList ||
        errorSchemaList ||
        errorCdlMutation ||
        errorTableList) && (
        <Error
          error={
            errorCatalogList ||
            errorSchemaList ||
            errorCdlMutation ||
            errorTableList
          }
        />
      )}

      <Modal
        show={isImportModalVisible}
        onHide={closeModal}
        enforceFocus={false}
        dialogClassName="modal-80w"
        aria-labelledby="importCDLTitle"
      >
        <Modal.Header closeButton>
          <Modal.Title id="importFromCdlTitle">
            Import from Clinical Data Lake into &quot;{activityData.trialNumber}
            /{props.currentFolder}&quot;
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container fluid>
            {(isLoadingCatalogList ||
              isLoadingSchemaList ||
              isLoadingCdlMutation ||
              isLoadingTableList) && (
              <div className="center_div">
                <Spinner animation="border" className="spinner_color" />
                <p>Loading...</p>
              </div>
            )}
            {catalogList &&
              selectedCatalog === null &&
              !isLoadingCdlMutation && (
                <>
                  <h2>Catalog List</h2>
                  <Row className="overflow-scroll catalog_list_cdl">
                    <DataGridPro
                      rows={catalogList?.getCdlCatalogList}
                      columns={catalogColumns}
                      autoHeight
                      disableSelectionOnClick
                      pagination
                      rowsPerPageOptions={[25, 50, 100]}
                      pageSize={pageSizeTable}
                      onPageSizeChange={(pageSize) =>
                        setPageSizeTable(pageSize)
                      }
                      sortModel={[{ field: "catalogType", sort: "desc" }]}
                      getRowId={(row) => row.name}
                    />
                  </Row>
                </>
              )}
            {selectedCatalog &&
              schemaList &&
              !selectedSchema &&
              !isLoadingCdlMutation && (
                <>
                  <h2>Schema List for Catalog {selectedCatalog}</h2>
                  <Row className="overflow-scroll schema_list_cdl">
                    <DataGridPro
                      rows={schemaList?.getCdlSchemaList}
                      columns={schemaColumns}
                      autoHeight
                      disableSelectionOnClick
                      pagination
                      rowsPerPageOptions={[25, 50, 100]}
                      pageSize={pageSizeTable}
                      onPageSizeChange={(pageSize) =>
                        setPageSizeTable(pageSize)
                      }
                      getRowId={(row) => row.fullName}
                      checkboxSelection
                      selectionModel={selectedSchemasForImport}
                      onSelectionModelChange={handleSelectMultipleSchemas}
                    />
                  </Row>
                  <Row>
                    <Col xs={3}>
                      <Button
                        variant="light"
                        onClick={() => {
                          setSelectedCatalog(null);
                          setSelectedSchemasForImport([]);
                          setSelectedSchema(null);
                        }}
                      >
                        <ArrowBackIcon className="w-auto align-self-center" />{" "}
                        Back
                      </Button>
                    </Col>
                    <Col xs={1}>QC Status:</Col>
                    <Col xs={2}>
                      <Form.Select
                        value={String(
                          getQualityCheckStatusKeyFromValue(qualityCheckStatus)
                        )}
                        onChange={changeQualityCheckedStatus}
                        key="cdlquality_controlled"
                        id="importFromCdlQualityFormSelect"
                      >
                        {Object.entries(QualityCheckStatusUI).map((element) => (
                          <option key={element[0]} value={element[0]}>
                            {element[1]}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={1}>
                      <Form.Label>Description:</Form.Label>
                    </Col>

                    <Col xs={2}>
                      <Form.Control
                        type="text"
                        required
                        placeholder="Enter description"
                        id="importFromCDLDescriptionFormControl"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </Col>

                    <Col xs={2}>
                      <Button
                        className="float-end"
                        disabled={selectedSchemasForImport.length <= 0}
                        onClick={importSchemaCallerSchema}
                        id="importFromCdlButtonTable"
                        data-testid="importFromCdlButton"
                      >
                        Import Schema
                        {selectedSchemasForImport.length > 1 && "s"}
                      </Button>
                    </Col>
                  </Row>
                </>
              )}

            {selectedSchema && tableList && !isLoadingCdlMutation && (
              <>
                <h2>
                  Table List for Schema {selectedCatalog}/{selectedSchema}
                </h2>
                <Row className="overflow-scroll schema_list_cdl">
                  <Col xs={selectedTable ? 7 : 12}>
                    <DataGridPro
                      rows={tableList?.getCdlTableList}
                      columns={tableColumns}
                      autoHeight
                      disableSelectionOnClick
                      pagination
                      rowsPerPageOptions={[25, 50, 100]}
                      pageSize={pageSizeTable}
                      onPageSizeChange={(pageSize) =>
                        setPageSizeTable(pageSize)
                      }
                      getRowId={(row) => row.fullName}
                      checkboxSelection
                      selectionModel={selectedTablesForImport}
                      onSelectionModelChange={handleSelectMultipleTables}
                    />
                  </Col>

                  {selectedTable && (
                    <>
                      <Col xs={4}>
                        <h3>Columns for Table {selectedTable}</h3>
                        <Table striped bordered hover>
                          <thead>
                            <tr>
                              <th>name</th>
                              <th>typeText</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableList?.getCdlTableList
                              .find(
                                (table: GetCdlTableOutput) =>
                                  table.name == selectedTable
                              )
                              ?.columns.map((columnData: CdlTableColumn) => (
                                <tr key={columnData.name}>
                                  <td>{columnData.name}</td>
                                  <td>{columnData.typeText}</td>
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </Col>
                      <Col xs={1}>
                        <Button
                          variant="link"
                          onClick={() => setSelectedTable(null)}
                          id="remove_selected_tab"
                          className="float-end close_or_remove_button fs-8"
                          size="sm"
                        >
                          X
                        </Button>
                      </Col>
                    </>
                  )}
                </Row>
                <Row>
                  <Col xs={3}>
                    <Button
                      variant="light"
                      onClick={() => {
                        setSelectedSchema(null);
                        setSelectedTablesForImport([]);
                        setSelectedTable(null);
                      }}
                    >
                      <ArrowBackIcon className="w-auto align-self-center" />{" "}
                      Back
                    </Button>
                  </Col>
                  <Col xs={1}>QC Status:</Col>
                  <Col xs={2}>
                    <Form.Select
                      value={String(
                        getQualityCheckStatusKeyFromValue(qualityCheckStatus)
                      )}
                      onChange={changeQualityCheckedStatus}
                      key="cdlquality_controlled"
                      id="importFromCdlQualityFormSelect"
                    >
                      {Object.entries(QualityCheckStatusUI).map((element) => (
                        <option key={element[0]} value={element[0]}>
                          {element[1]}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={1}>
                    <Form.Label>Description:</Form.Label>
                  </Col>

                  <Col xs={2}>
                    <Form.Control
                      type="text"
                      required
                      placeholder="Enter description"
                      id="importFromCDLDescriptionFormControl"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </Col>

                  <Col xs={2}>
                    <Button
                      className="float-end"
                      disabled={selectedTablesForImport.length <= 0}
                      onClick={importSchemaCallerSchema}
                      id="importFromCdlButtonTable"
                      data-testid="importFromCdlButtonTable"
                    >
                      Import Table
                      {selectedTablesForImport.length > 1 && "s"}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </Container>
        </Modal.Body>
      </Modal>
    </>
  );
}
